import React, { useEffect, useState } from "react";
import { Search, Store, MapPin, ShieldCheck, Eye, Download, Filter, Phone } from "lucide-react";
import api from "../lib/api";
import { EmptyState, Skeleton, Pagination, usePagination } from "../components/UI";
import { useNavigate } from "react-router-dom";

export default function SuppliersPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [verified, setVerified] = useState("all");
  const [kota, setKota] = useState("");

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (verified !== "all") params.verified = verified;
    if (kota) params.kota = kota;
    const { data } = await api.get("/suppliers", { params });
    setSuppliers(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, verified, kota]);

  const filtered = suppliers;
  const { page, setPage, perPage, changePerPage, totalPages, paged, totalItems } = usePagination(filtered, 10);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [q, verified, kota, setPage]);

  const exportCsv = () => {
    if (suppliers.length === 0) return;
    const headers = ["Nama Usaha", "Jenis Produk", "Kota", "Kecamatan", "Kelurahan", "Alamat", "No HP", "Status Verifikasi"];
    const rows = suppliers.map((s) => [
      s.nama_usaha,
      (s.jenis_produk || []).join("; "),
      s.kota,
      s.kecamatan || "",
      s.kelurahan || "",
      s.alamat,
      s.no_hp,
      s.verified ? "Terverifikasi" : "Belum Verifikasi",
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `daftar-supplier-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="kedan-label text-slate-500">Direktori</div>
          <h1 className="text-3xl font-display font-bold text-slate-900 mt-1">Supplier & Katalog</h1>
          <p className="text-slate-500 text-sm mt-1">Pencarian, filter, dan profil usaha penyedia komoditas.</p>
        </div>
        <button onClick={exportCsv} className="kedan-btn-primary" data-testid="export-supplier-csv">
          <Download className="w-4 h-4" /> Ekspor CSV
        </button>
      </header>

      {/* Filter Bar */}
      <div className="kedan-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-slate-400" />
          <div className="flex gap-1.5">
            {[
              { key: "all", label: "Semua" },
              { key: "true", label: "Terverifikasi" },
              { key: "false", label: "Belum Verifikasi" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setVerified(f.key)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  verified === f.key
                    ? "bg-[#045498] text-white border-[#045498]"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`filter-supplier-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input className="kedan-input !w-40 !py-1.5 text-xs" placeholder="Kota..."
            value={kota} onChange={(e) => setKota(e.target.value)} data-testid="supplier-filter-kota" />
          <div className="relative ml-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              className="kedan-input !w-64 !py-1.5 !pl-8 text-xs"
              placeholder="Cari nama usaha, produk, jenis, kota, alamat..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="supplier-search"
            />
          </div>
          <div className="text-xs text-slate-500">{suppliers.length} supplier</div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : suppliers.length === 0 ? (
          <EmptyState title="Tidak ada supplier" description="Coba ubah filter atau kata kunci pencarian." icon={Store} />
        ) : (<>
          <table className="kedan-table" data-testid="supplier-table">
            <thead><tr>
              <th>No</th>
              <th>Nama Usaha</th>
              <th>Jenis Produk</th>
              <th>Lokasi</th>
              <th>No HP</th>
              <th>Status</th>
              <th className="text-center">Aksi</th>
            </tr></thead>
            <tbody data-testid="supplier-table-body">
              {paged.map((s, idx) => (
                <tr key={s.id} className="group" data-testid={`supplier-row-${s.id}`}>
                  <td className="text-sm text-slate-500 font-mono">{(page - 1) * perPage + idx + 1}</td>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-md bg-blue-50 text-[#045498] flex items-center justify-center shrink-0">
                        <Store className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{s.nama_usaha}</div>
                        {s.nama_pic && <div className="text-xs text-slate-500 mt-0.5">PIC: {s.nama_pic}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="text-sm text-slate-600">{(s.jenis_produk || []).join(", ")}</td>
                  <td>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>
                        {s.kota}
                        {s.kecamatan && `, ${s.kecamatan}`}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5 line-clamp-1 max-w-[220px]">{s.alamat}</div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5 text-sm text-slate-600 font-mono">
                      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                      {s.no_hp}
                    </div>
                  </td>
                  <td>
                    {s.verified ? (
                      <span className="kedan-badge status-aktif inline-flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Verified
                      </span>
                    ) : (
                      <span className="kedan-badge status-nonaktif inline-flex items-center gap-1">
                        Belum
                      </span>
                    )}
                  </td>
                  <td className="text-center">
                    <button onClick={() => navigate(`/suppliers/${s.id}`)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-blue-50 text-[#045498] hover:bg-blue-100 border border-blue-200 transition-colors"
                      data-testid={`view-supplier-${s.id}`}>
                      <Eye className="w-3.5 h-3.5" /> Lihat Katalog
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} totalItems={totalItems} perPage={perPage} onPageChange={setPage} onPerPageChange={changePerPage} />
        </>)}
      </div>
    </div>
  );
}
